/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import { map, filter, of, mergeMap, switchMap, catchError } from "rxjs";
import { Epic } from "redux-observable";

import { Action, ActionType } from "lib/ActionType";

import ServicesContext from "services/ServicesContext";

import * as companiesSlice from "./companies.slice";
import * as assetGroupsSlice from "./assetGroups.slice";
import * as availableAssetFiltersSlice from "./availableAssetFilters.slice";
import * as selectedAssetFiltersSlice from "./selectedAssetFilters.slice";
import * as cvesSlice from "./cves.slice";
import { _fixVendorProduct, _humanizeVendorsProducts } from "./availableAssetFilters.epics";

type ActionsType = ActionType<typeof assetGroupsSlice.actions> | ActionType<typeof companiesSlice.actions>;

type State = {
   companies: companiesSlice.CompaniesState,
   assetGroups: assetGroupsSlice.AssetGroupsState,
   availableAssetFilters: availableAssetFiltersSlice.AvailableAssetFiltersState,
   selectedAssetFilters: selectedAssetFiltersSlice.SelectedAssetFiltersState
}

type TEpic<I extends Action<any> = any, O extends I & ActionsType = any> = Epic<I, O, State, ServicesContext>;


const init: TEpic<{ type: string }> = (action$, state) => {

   return action$.pipe(
      filter (
         action => action.type === "init"
      ),
      map(() =>
         companiesSlice.actions.loadCompanies()
      )
   )

}


const companiesLoaded: TEpic<ActionType<typeof companiesSlice.actions.companiesLoaded>> = (action$, state) => {

   return action$.pipe(
      filter (
         action => action.type === companiesSlice.actions.companiesLoaded.type
      ),
      map(() =>
         state.value.companies.companies.length > 0 ?
            companiesSlice.actions.selectCompanyById({ id: state.value.companies.companies[0].id })
         :
            companiesSlice.actions.selectCompanyById({ id: "" })
      )
   )

}


const selectCompany: TEpic<
   ActionType<
      typeof companiesSlice.actions.selectCompanyById |
      typeof companiesSlice.actions.selectCompanyByName
   >
> = (action$, state) => {

   return action$.pipe(
      filter(
         action =>
            action.type === companiesSlice.actions.selectCompanyById.type ||
            action.type === companiesSlice.actions.selectCompanyByName.type
      ),
      switchMap(
         () => {
            return (
               state.value.companies.selectedCompany ?
                  of (
                     selectedAssetFiltersSlice.actions.loadAssetFilters({ companyId: state.value.companies.selectedCompany.id }),
                     assetGroupsSlice.actions.loadAssetGroups({ companyId: state.value.companies.selectedCompany.id }),
                     assetGroupsSlice.actions.selectAssetGroupById({ id: ""}),
                     cvesSlice.actions.setCompany({ companyId: state.value.companies.selectedCompany.id }),
                  )
               :
                  of(assetGroupsSlice.actions.clearAssetGroups())
            )
         })
   )

}

const preSelectAssetGroup: TEpic<
   ActionType<
      typeof assetGroupsSlice.actions.selectAssetGroupById |
      typeof assetGroupsSlice.actions.selectAssetGroupByName
   >
> = (action$, state, deps) => {

   return action$.pipe(
      filter(
         action =>
            action.type === assetGroupsSlice.actions.selectAssetGroupById.type ||
            action.type === assetGroupsSlice.actions.selectAssetGroupByName.type
      ),
      map(
         () => selectedAssetFiltersSlice.actions.setAssetFiltersLoading({ filtersLoading: true })
      )
   )
}

const selectAssetGroup: TEpic<
   ActionType<
      typeof assetGroupsSlice.actions.selectAssetGroupById |
      typeof assetGroupsSlice.actions.selectAssetGroupByName
   >
> = (action$, state, deps) => {

   return action$.pipe(
      filter(
         action =>
            action.type === assetGroupsSlice.actions.selectAssetGroupById.type ||
            action.type === assetGroupsSlice.actions.selectAssetGroupByName.type
      ),
      switchMap(

         action => state.value.companies.selectedCompany && state.value.assetGroups.selectedAssetGroup ?

            deps.api.companyDataProvider.copyAssetGroupFilters(
               state.value.companies.selectedCompany.id,
               state.value.assetGroups.selectedAssetGroup.id,
               "_",
               true
            ).pipe(
               map(() => selectedAssetFiltersSlice.actions.loadAssetFilters({ companyId: state.value.companies.selectedCompany!.id }))
            )

         :

            deps.api.companyDataProvider.deleteAssetGroupFilters(
               state.value.companies.selectedCompany!.id,
               "_"
            ).pipe(
               map(() => selectedAssetFiltersSlice.actions.loadAssetFilters({ companyId: state.value.companies.selectedCompany!.id }))
            )

      ),
      catchError(err => of(selectedAssetFiltersSlice.actions.setAssetFiltersLoading({ filtersLoading: false })))
   )

}

const assetFiltersLoaded: TEpic<typeof selectedAssetFiltersSlice.actions.assetFiltersLoaded> = (action$, state) => {

   return action$.pipe(
      filter(
         action => action.type === selectedAssetFiltersSlice.actions.assetFiltersLoaded.type
      ),
      map(
         () => cvesSlice.actions.loadCves({})
      )
   )

}


const selectAssetFilter: TEpic<typeof selectedAssetFiltersSlice.actions.selectAssetFilter> = (action$, state) => {

   return action$.pipe(
      filter(
         action => action.type === selectedAssetFiltersSlice.actions.selectAssetFilter.type
      ),
      mergeMap(
         () => {
            if (state.value.selectedAssetFilters.selectedFilter) {
               const vendor = _humanizeVendorsProducts([ state.value.selectedAssetFilters.selectedFilter.vendor])[0];
               const product = _humanizeVendorsProducts([ state.value.selectedAssetFilters.selectedFilter.product])[0];

               if (state.value.availableAssetFilters.selectedVendor === vendor) {
                  return of(
                     availableAssetFiltersSlice.actions.selectProduct({ product }),
                  )
               } else {
                  return of(
                     availableAssetFiltersSlice.actions.selectVendor({ vendor }),
                     availableAssetFiltersSlice.actions.scheduleSelectProduct({ product })
                  )
               }
            } else {
               return of({ type: "" })
            }
         }
      )
   )

}

const addAvailableAssetFilterCriteria: TEpic<ActionType<typeof availableAssetFiltersSlice.actions.addAvailableAssetFilterCriteria>> = (action$, state, deps) => {

   return action$.pipe(
      filter(
         action => action.type === availableAssetFiltersSlice.actions.addAvailableAssetFilterCriteria.type
      ),
      map(
         action => {

            if (!state.value.companies.selectedCompany) return { type: "" };

            const vendor = _fixVendorProduct(state.value.availableAssetFilters.selectedVendor);
            const product = _fixVendorProduct(state.value.availableAssetFilters.selectedProduct);

            // try to find filter first

            let f = state.value.selectedAssetFilters.filters.find( f => {
               return (
                  _fixVendorProduct(f.vendor) === vendor &&
                  _fixVendorProduct(f.product) === product
               )
            })

            // if filter found, create desc for the filter, but first check if the desc does not exist already

            if (f) {

               const fd = state.value.selectedAssetFilters.filterDescs[f.id].find(
                  fd =>
                     fd.field === action.payload.field &&
                     fd.operator === action.payload.operator &&
                     fd.value === action.payload.value &&
                     fd.negate === action.payload.negate
               )

               if (fd) return { type: "" };

               return selectedAssetFiltersSlice.actions.createAssetFilterDesc({
                  companyId: f.companyId,
                  filterId: f.id,
                  field: action.payload.field,
                  operator: action.payload.operator,
                  value: action.payload.value,
                  negate: action.payload.negate
               });

            }


            // if filter not found, create it first then create the desc

            return selectedAssetFiltersSlice.actions.createAssetFilterAndDesc({
               companyId: state.value.companies.selectedCompany.id,
               vendor: vendor,
               product: product,
               field: action.payload.field,
               operator: action.payload.operator,
               value: action.payload.value,
               negate: action.payload.negate
            })

         })
   )

}

const loadCvesAfterVariousActions: TEpic<
   ActionType<
      typeof selectedAssetFiltersSlice.actions.assetFilterCreated |
      typeof selectedAssetFiltersSlice.actions.assetFilterDescCreated |
      typeof selectedAssetFiltersSlice.actions.assetFilterDeleted |
      typeof selectedAssetFiltersSlice.actions.assetFilterDescDeleted
   >
> = (action$, state) => {

   return action$.pipe(
      filter(
         action =>
            action.type === selectedAssetFiltersSlice.actions.assetFilterCreated.type ||
            action.type === selectedAssetFiltersSlice.actions.assetFilterDescCreated.type ||
            action.type === selectedAssetFiltersSlice.actions.assetFilterDeleted.type ||
            action.type === selectedAssetFiltersSlice.actions.assetFilterDescDeleted.type
      ),
      map(() => cvesSlice.actions.loadCves({}))
   )

}

const epics = [
   init,
   companiesLoaded,
   selectCompany,
   preSelectAssetGroup,
   selectAssetGroup,
   assetFiltersLoaded,
   selectAssetFilter,
   addAvailableAssetFilterCriteria,
   loadCvesAfterVariousActions
];


export default epics;